import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface CoverageRow {
  lesson_id: string;
  lesson_title: string | null;
  course: string | null;
  is_beta_content: boolean;
  hidden: boolean;
  book_insight_count: number;
  company_case_count: number;
  primary_book_insight_count: number;
  is_test_fixture: boolean;
  coverage_status: string;
  issue_flags: string[] | null;
}

type StatusFilter = "all" | "gaps" | "primary" | "hidden";

export function InsightsCoverageCard() {
  const [rows, setRows] = useState<CoverageRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("gaps");
  const [showTestFixture, setShowTestFixture] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    // View not in generated types — cast to any for the query builder.
    const { data, error } = await (supabase)
      .from("insights_coverage_report")
      .select(
        "lesson_id, lesson_title, course, is_beta_content, hidden, book_insight_count, company_case_count, primary_book_insight_count, is_test_fixture, coverage_status, issue_flags",
      );
    if (error) {
      setError(error.message);
      setRows(null);
    } else {
      setRows((data ?? []) as CoverageRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const summary = useMemo(() => {
    if (!rows) return null;
    const real = rows.filter((r) => !r.is_test_fixture);
    const beta = real.filter((r) => r.is_beta_content);
    const nonBeta = real.filter((r) => !r.is_beta_content);
    const betaStrong = beta.filter((r) => r.coverage_status === "strong").length;
    const nonBetaStrong = nonBeta.filter((r) => r.coverage_status === "strong").length;
    const gapRows = real.filter(
      (r) => r.coverage_status !== "strong" && !r.hidden,
    );
    const duplicatePrimary = real.filter(
      (r) => r.coverage_status === "duplicate_primary",
    ).length;
    const missingPrimary = real.filter(
      (r) => r.coverage_status === "missing_primary",
    ).length;
    const hiddenWithInsights = rows.filter(
      (r) => r.coverage_status === "hidden_with_insights",
    ).length;
    const testFixtureCount = rows.filter((r) => r.is_test_fixture).length;
    const healthy =
      gapRows.length === 0 &&
      duplicatePrimary === 0 &&
      missingPrimary === 0 &&
      hiddenWithInsights === 0;
    return {
      betaStrong,
      betaTotal: beta.length,
      nonBetaStrong,
      nonBetaTotal: nonBeta.length,
      gapCount: gapRows.length,
      duplicatePrimary,
      missingPrimary,
      hiddenWithInsights,
      testFixtureCount,
      healthy,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!rows) return [];
    let base = rows.filter((r) => !r.is_test_fixture);
    if (filter === "gaps") {
      base = base.filter(
        (r) => r.coverage_status !== "strong" && !r.hidden,
      );
    } else if (filter === "primary") {
      base = base.filter(
        (r) =>
          r.coverage_status === "duplicate_primary" ||
          r.coverage_status === "missing_primary",
      );
    } else if (filter === "hidden") {
      base = rows.filter((r) => r.coverage_status === "hidden_with_insights");
    }
    return base.sort((a, b) => {
      if (a.is_beta_content !== b.is_beta_content)
        return a.is_beta_content ? -1 : 1;
      const c = (a.course ?? "").localeCompare(b.course ?? "");
      if (c !== 0) return c;
      return (a.lesson_title ?? "").localeCompare(b.lesson_title ?? "");
    });
  }, [rows, filter]);

  const testFixtures = useMemo(
    () => (rows ?? []).filter((r) => r.is_test_fixture),
    [rows],
  );

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Insights Coverage</h2>
          <p className="text-xs text-muted-foreground">
            Read-only report from{" "}
            <code className="font-mono">insights_coverage_report</code>. TEST
            fixtures are excluded from real curriculum totals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <Badge
              variant={summary.healthy ? "secondary" : "destructive"}
              className="text-xs"
            >
              {summary.healthy ? "Healthy" : "Attention needed"}
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          Failed to load coverage report: {error}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat
            label="Beta strong"
            value={`${summary.betaStrong} / ${summary.betaTotal}`}
            ok={summary.betaStrong === summary.betaTotal}
          />
          <Stat
            label="Non-beta strong"
            value={`${summary.nonBetaStrong} / ${summary.nonBetaTotal}`}
            ok={summary.nonBetaStrong === summary.nonBetaTotal}
          />
          <Stat
            label="Real curriculum gaps"
            value={String(summary.gapCount)}
            ok={summary.gapCount === 0}
          />
          <Stat
            label="Duplicate primary"
            value={String(summary.duplicatePrimary)}
            ok={summary.duplicatePrimary === 0}
          />
          <Stat
            label="Missing primary"
            value={String(summary.missingPrimary)}
            ok={summary.missingPrimary === 0}
          />
          <Stat
            label="Hidden w/ insights"
            value={String(summary.hiddenWithInsights)}
            ok={summary.hiddenWithInsights === 0}
          />
          <Stat
            label="Test fixture excluded"
            value={String(summary.testFixtureCount)}
            ok
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-muted-foreground">Filter:</span>
        {(
          [
            ["gaps", "Gaps only"],
            ["primary", "Primary issues"],
            ["hidden", "Hidden w/ insights"],
            ["all", "All (non-test)"],
          ] as [StatusFilter, string][]
        ).map(([id, label]) => (
          <Button
            key={id}
            size="sm"
            variant={filter === id ? "secondary" : "ghost"}
            onClick={() => setFilter(id)}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="overflow-x-auto">
        {loading && !rows ? (
          <p className="text-xs text-muted-foreground p-4">Loading…</p>
        ) : visibleRows.length === 0 ? (
          <p className="text-xs text-muted-foreground p-4">
            {filter === "gaps"
              ? "All real curriculum lessons have strong Insights coverage."
              : "No rows match this filter."}
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left p-2">Lesson</th>
                <th className="text-left p-2">Course</th>
                <th className="text-left p-2">Beta</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Flags</th>
                <th className="text-right p-2">Books</th>
                <th className="text-right p-2">Cases</th>
                <th className="text-right p-2">Primary</th>
                <th className="text-left p-2">Hidden</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.lesson_id} className="border-t border-border/40">
                  <td className="p-2">{r.lesson_title ?? r.lesson_id.slice(0, 8)}</td>
                  <td className="p-2 text-muted-foreground">{r.course ?? "—"}</td>
                  <td className="p-2">{r.is_beta_content ? "beta" : ""}</td>
                  <td className="p-2">
                    <Badge
                      variant={r.coverage_status === "strong" ? "secondary" : "destructive"}
                      className="text-[10px]"
                    >
                      {r.coverage_status}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <span className="flex flex-wrap gap-1">
                      {(r.issue_flags ?? []).map((f) => (
                        <Badge key={f} variant="outline" className="text-[10px]">
                          {f}
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td className="p-2 text-right">{r.book_insight_count}</td>
                  <td className="p-2 text-right">{r.company_case_count}</td>
                  <td className="p-2 text-right">{r.primary_book_insight_count}</td>
                  <td className="p-2 text-muted-foreground">{r.hidden ? "hidden" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {testFixtures.length > 0 && (
        <div className="border-t border-border/40 pt-3">
          <button
            type="button"
            className="text-xs text-muted-foreground underline"
            onClick={() => setShowTestFixture((v) => !v)}
          >
            {showTestFixture ? "Hide" : "Show"} excluded test fixtures (
            {testFixtures.length})
          </button>
          {showTestFixture && (
            <ul className="mt-2 text-xs text-muted-foreground space-y-1">
              {testFixtures.map((r) => (
                <li key={r.lesson_id}>
                  <span className="font-mono">{r.lesson_id.slice(0, 8)}</span>{" "}
                  · {r.course ?? "—"} · {r.lesson_title ?? "(untitled)"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <div
      className={`rounded border p-2 ${
        ok ? "border-border/40" : "border-destructive/40 bg-destructive/5"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-base font-semibold">{value}</div>
    </div>
  );
}